import { useCallback, useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import {
  AREA_TYPES,
  DEFAULT_MODIFIERS,
  DEFAULT_PLANT_DENSITY,
  getAreaTypeColor,
  getSuggestedServices,
  hasPreEmergentService,
  PLACEMENT_ITEM_TYPES,
  withoutPreEmergentService,
} from '../../SiteTakeoff/siteTakeoffConstants';

export default function usePlacementQueue({
  mapRef,
  mapContainerRef,
  placementQueue = [],
  addArea,
  updatePlacementQueue,
  setEditingAreaId,
}) {
  const [placingItemId, setPlacingItemId] = useState(null);
  const [placementPaused, setPlacementPaused] = useState(false);
  const placingItemIdRef = useRef(null);
  const queueRef = useRef([]);

  useEffect(() => { placingItemIdRef.current = placingItemId; }, [placingItemId]);
  useEffect(() => { queueRef.current = placementQueue || []; }, [placementQueue]);

  useEffect(() => {
    if (!placingItemId) return undefined;
    const onDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        setPlacementPaused(true);
      }
    };
    const onUp = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        setPlacementPaused(false);
      }
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      setPlacementPaused(false);
    };
  }, [placingItemId]);

  const placeCurrentItemAt = useCallback((clientX, clientY) => {
    const map = mapRef.current;
    const currentPlacingId = placingItemIdRef.current;
    const container = mapContainerRef?.current || map?.getContainer?.();
    if (!map || !container || !currentPlacingId) return;

    const item = queueRef.current.find(q => q.queueId === currentPlacingId);
    if (!item) return;

    const rect = container.getBoundingClientRect();
    const point = L.point(clientX - rect.left, clientY - rect.top);
    const ll = map.containerPointToLatLng(point);
    const typeDef = PLACEMENT_ITEM_TYPES[item.itemType] || PLACEMENT_ITEM_TYPES.tree;
    const areaType = item.areaType || typeDef.defaultAreaType || AREA_TYPES.POINT_MARKER;
    const color = getAreaTypeColor(areaType);
    const suggestedServices = getSuggestedServices(areaType);

    let treeDetails;
    if (item.itemType === 'tree') {
      treeDetails = {
        species: item.details?.species || item.details?.commonName || '',
        height: item.details?.height || 0,
        caliper: item.details?.caliper || 0,
        canopySpread: item.details?.canopySpread || item.details?.spread || 0,
        condition: item.details?.condition || '',
      };
    }

    const created = addArea({
      geometryType: 'point',
      name: item.label || typeDef.label,
      areaType,
      paths: [{ lat: ll.lat, lng: ll.lng }],
      area: '0',
      areaAcres: '0',
      perimeter: '0',
      fillColor: color,
      strokeColor: '#000000',
      treeDetails,
      plantDensity: {
        ...DEFAULT_PLANT_DENSITY,
        treeCount: item.itemType === 'tree' ? 1 : 0,
      },
      modifiers: { ...DEFAULT_MODIFIERS },
      servicesRequired: withoutPreEmergentService(suggestedServices),
      preEmergentEnabled: hasPreEmergentService(suggestedServices),
      notes: item.details?._raw
        ? Object.entries(item.details._raw)
          .map(([k, v]) => (v ? `${k}: ${v}` : ''))
          .filter(Boolean)
          .join(' | ')
        : '',
      queueItemId: currentPlacingId,
    }, { skipUndo: true });

    updatePlacementQueue(prev => {
      const currentIdx = prev.findIndex(q => q.queueId === currentPlacingId);
      const next = prev.map(q => (q.queueId === currentPlacingId
        ? { ...q, status: 'placed', areaId: created.id }
        : q));
      const afterCurrent = next.slice(currentIdx + 1);
      const beforeCurrent = next.slice(0, currentIdx);
      const nextUnplaced = afterCurrent.find(q => q.status === 'unplaced')
        || beforeCurrent.find(q => q.status === 'unplaced');
      setPlacingItemId(nextUnplaced?.queueId || null);
      return next;
    }, 'Place queue item');

    if (setEditingAreaId) setEditingAreaId(created.id);
  }, [addArea, mapContainerRef, mapRef, setEditingAreaId, updatePlacementQueue]);

  const handlePlacementOverlayClick = useCallback((e) => {
    if (placementPaused) return;
    placeCurrentItemAt(e.clientX, e.clientY);
  }, [placeCurrentItemAt, placementPaused]);

  return {
    placingItemId,
    setPlacingItemId,
    placementPaused,
    handlePlacementOverlayClick,
  };
}
